import React, { useState } from "react";
import { Mail, Wand2, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * MagicLinkInline · Director directive 2026.02 (Strike #18)
 *
 * Compact, drop-in magic-link request widget. Zero-friction email
 * login as the SECONDARY auth fallback when password / cookie flow
 * fails. Used on:
 *   • <GhostBridge /> primary conversion panel
 *   • Anywhere we need a "just send me a link" recovery path
 *
 * Self-contained:
 *   • Owns its own email + busy + error + sent state
 *   • POSTs to /api/auth/magic-link/request (same endpoint as Login.js)
 *   • Mobile-first: 44px touch targets, stacked layout below sm,
 *     16px input font-size (iOS won't zoom on focus), `enterkeyhint`
 *     for the on-screen keyboard "Send" affordance.
 *
 * Renders a success-state confirmation after submission so the
 * Commander knows the email is on its way without leaving the panel.
 */
export default function MagicLinkInline({
    accent = "gold",
    placeholder = "operator@yourdomain.com",
    label = "Email me a one-tap login link",
    testidPrefix = "magic-inline",
}) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [sent, setSent] = useState(false);

    const isGold = accent === "gold";
    const ring = isGold ? "#E8C56C" : "#00E5FF";
    const glow = isGold ? "rgba(232,197,108,0.45)" : "rgba(0,229,255,0.5)";

    const submit = async (e) => {
        e?.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) {
            setErr("Enter your email to receive a link.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await api.post("/auth/magic-link/request", {
                email: trimmed,
                base_url: window.location.origin,
            });
            setSent(true);
        } catch (ex) {
            setErr(
                formatApiError(ex.response?.data?.detail) ||
                    ex.message ||
                    "Could not request a magic link. Try again in a moment."
            );
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <div
                data-testid={`${testidPrefix}-sent`}
                className="flex items-start gap-3 p-4 rounded-md"
                style={{
                    background: `${isGold ? "rgba(232,197,108,0.08)" : "rgba(0,229,255,0.08)"}`,
                    border: `1px solid ${ring}55`,
                }}
            >
                <CheckCircle2 size={18} style={{ color: ring, flexShrink: 0, marginTop: 2 }} />
                <div className="text-sm leading-relaxed" style={{ color: "#C2D5EA" }}>
                    <span style={{ color: ring, fontWeight: 700 }}>Signal sent.</span>{" "}
                    Check{" "}
                    <span className="font-mono text-[#F8FAFC]">{email}</span> — the magic link
                    arrives within 30 seconds. Tap it from any device to board.
                    <button
                        type="button"
                        onClick={() => {
                            setSent(false);
                            setEmail("");
                        }}
                        data-testid={`${testidPrefix}-resend`}
                        className="block mt-2 text-[10px] uppercase tracking-[0.25em]"
                        style={{ color: ring, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                    >
                        Send to a different email →
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form
            data-testid={`${testidPrefix}-form`}
            onSubmit={submit}
            className="flex flex-col gap-2"
            noValidate
        >
            <label
                htmlFor={`${testidPrefix}-email`}
                className="text-[10px] uppercase tracking-[0.3em] flex items-center gap-1.5"
                style={{ color: ring, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
            >
                <Wand2 size={11} /> {label}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Mail
                        size={15}
                        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ color: "#5C7894" }}
                    />
                    <input
                        id={`${testidPrefix}-email`}
                        data-testid={`${testidPrefix}-email`}
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        enterKeyHint="send"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={placeholder}
                        disabled={busy}
                        className="w-full pl-9 pr-3 py-3 text-base rounded-md focus:outline-none transition-colors"
                        style={{
                            background: "rgba(5,10,15,0.55)",
                            border: `1px solid #1E3654`,
                            color: "#F8FAFC",
                            fontSize: "16px", // iOS won't zoom on focus
                            minHeight: 44,
                        }}
                        onFocus={(e) => {
                            e.currentTarget.style.borderColor = ring;
                            e.currentTarget.style.boxShadow = `0 0 0 1px ${ring}55`;
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = "#1E3654";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid={`${testidPrefix}-submit`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 uppercase tracking-[0.22em] text-xs font-bold transition-all duration-300 disabled:opacity-60"
                    style={{
                        fontFamily: "'Montserrat', sans-serif",
                        background: ring,
                        color: "#050A0F",
                        border: `1px solid ${ring}`,
                        minHeight: 44,
                        flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                        if (!busy) e.currentTarget.style.boxShadow = `0 0 20px ${glow}`;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                    }}
                >
                    {busy ? (
                        <>
                            <Loader2 size={13} className="animate-spin" /> Sending
                        </>
                    ) : (
                        <>
                            <Wand2 size={13} /> Send Link
                        </>
                    )}
                </button>
            </div>
            {err && (
                <div
                    data-testid={`${testidPrefix}-error`}
                    className="text-xs leading-relaxed"
                    style={{ color: "#FF6B6B" }}
                >
                    {err}
                </div>
            )}
            <div className="text-[10px] leading-relaxed" style={{ color: "#5C7894" }}>
                No password required. Works on any device — your phone, your war-room
                workstation, or a teammate&rsquo;s tablet.
            </div>
        </form>
    );
}
